import { create } from 'zustand'

import { findIndex, has } from 'lodash'
import {
    deleteHardReply,
    deleteReply,
    deleteThread,
    getRequestDetail,
    getSharedReplyList,
    getSharedThread,
    patchThead,
    postRateReply,
    postReply,
    postThead,
    removeReturnReply,
} from '../api/request'
import {
    BodyCreateReplyApiV1CollaborationAppReplyPost,
    BodyCreateThreadApiV1ChatAppThreadPost,
    BodyRateReplyApiV1ChatAppReplyReplyIdRatePost,
    RateReplyApiV1ChatAppReplyReplyIdRatePostParams,
    ReplyContentTextDTO,
    ReplyDTO,
    ReplyListApiV1ChatAppReplyGetParams,
    ThreadDTO,
    ThreadDeleteApiV1ChatAppThreadThreadIdDeleteParams,
    ThreadDeleteOutputDTO,
    ThreadDetailApiV1ChatAppThreadThreadIdGetParams,
    ThreadUpdateRequestModel,
    UpdateThreadApiV1ChatAppThreadThreadIdPutParams,
} from '../swagger/dto'
import { RequestSettingVoiceAnswer } from '../types/requests'

interface RequestsState {
    openRequestSetting: boolean
    setOpenRequestSetting: (isOpen: boolean) => void

    requestSettingAdditionalContexts?: any
    setRequestSettingAdditionalContexts: (requestSetting: any) => void

    requestSettingParams?: any
    setRequestSettingParams: (requestSetting: any) => void

    requestMemoryMessageCount?: any
    setRequestSettingMemoryMessageCount: (requestSetting: any) => void

    requestSettingRagEnabled?: any
    setRequestSettingRagEnabled: (requestSetting: any) => void

    requestSettingVoiceAnswer?: RequestSettingVoiceAnswer
    setRequestSettingVoiceAnswer: (requestSetting: RequestSettingVoiceAnswer) => void

    requestSettingContext?: any
    setRequestSettingContext: (requestSetting: any) => void

    requestSettingLlmTools?: any
    setRequestSettingLlmTools: (requestSetting: any) => void

    requestDetail?: ThreadDTO
    isLoadingRequestDetail: boolean
    errorRequestDetail?: string
    fetchRequestDetail: (
        params: ThreadDetailApiV1ChatAppThreadThreadIdGetParams,
        shared?: boolean,
    ) => Promise<ThreadDTO>

    requestMessages?: ReplyDTO[]
    isLoadingSharedThreadMessages: boolean
    errorRequestMessages?: string
    fetchSharedThreadMessages: (params?: ReplyListApiV1ChatAppReplyGetParams) => Promise<void>
    setRequestMessages: (data: ReplyDTO[], total: number) => void

    isCreateRequest: boolean
    isLoadingCreateRequest: boolean
    errorCreateRequest?: string
    createRequest: (
        params: BodyCreateThreadApiV1ChatAppThreadPost,
    ) => Promise<ThreadDTO | undefined>

    isRemoveRequest: boolean
    isLoadingRemoveRequest: boolean
    errorRemoveRequest?: string
    removeRequest: (
        params: ThreadDeleteApiV1ChatAppThreadThreadIdDeleteParams,
    ) => Promise<ThreadDeleteOutputDTO>

    isLoadingUpdateRequest: boolean
    errorUpdateRequest?: string
    updateRequest: (
        params: UpdateThreadApiV1ChatAppThreadThreadIdPutParams,
        payload: ThreadUpdateRequestModel,
    ) => Promise<ReplyDTO>

    isCreateRequestMessage: boolean
    isLoadingCreateRequestMessage: boolean
    errorCreateRequestMessage?: string
    createRequestMessage: (
        params: BodyCreateReplyApiV1CollaborationAppReplyPost,
        min_balance: number,
    ) => Promise<ReplyDTO>

    isLoadingCreateRateReply: boolean
    errorCreateRateReply?: string
    createRateReply: (
        params: RateReplyApiV1ChatAppReplyReplyIdRatePostParams,
        payload: BodyRateReplyApiV1ChatAppReplyReplyIdRatePost,
    ) => Promise<ReplyDTO>

    isDoneRemoveReply: boolean
    isLoadingRemoveReply: boolean
    errorRemoveReply?: string
    removeReply: (replyIds: number[], softDelete: boolean) => void

    isCanFilesInRequest: boolean
    setIsCanFilesInRequest: (isCan: boolean) => void
    setFilesInRequest: (filesUrl: string[]) => void
    filesInRequest?: string[]
    setFile: (files: File[]) => void
    setFilesByStorage: (files: File[]) => void
    files?: File[]
    filesByStorage?: File[]

    maxFileCountByType?: object
    setMaxFileCountByType: (files) => void

    filesError?: string
    setFilesError: (error) => void

    availableExtensionsIsRequired?: string
    setAvailableExtensionsIsRequired: () => void

    clearRequest: () => void
    clearRequestSetting: () => void
    isShowRegistrationDialog: boolean
    messageCitations: ReplyContentTextDTO['citations']
    setMessageCitations: (messageCitations: ReplyContentTextDTO['citations']) => void
    setIsDoneUpdateRequest: (isDone: boolean) => void
    totalMessageCount: number

    updateRegularRequestsCountInRequestDetail: (
        params: ThreadDetailApiV1ChatAppThreadThreadIdGetParams,
    ) => Promise<void>

    errorRegenerateRequestMessage?: string
    regenerateRequestMessage: (
        params: BodyCreateReplyApiV1CollaborationAppReplyPost,
        min_balance: number,
        isEditText?: boolean,
    ) => Promise<void>
}

export const useRequestsStore = create<RequestsState>((set, get) => ({
    isShowRegistrationDialog: false,
    files: [],
    setFile: (files) => {
        set({
            files: files,
        })
    },

    filesByStorage: [],
    setFilesByStorage: (files) => {
        set({
            filesByStorage: files,
        })
    },

    maxFileCountByType: null,
    setMaxFileCountByType: (files) => {
        set({
            maxFileCountByType: files,
        })
    },
    filesError: null,
    setFilesError: (error) => {
        set({
            filesError: error,
        })
    },

    availableExtensionsIsRequired: null,
    setAvailableExtensionsIsRequired: (value) => {
        set({
            availableExtensionsIsRequired: value,
        })
    },

    requestSettingParams: undefined,
    setRequestSettingParams: (requestSetting) => {
        set({
            requestSettingParams: requestSetting,
        })
    },

    filesInRequest: undefined,
    setFilesInRequest: (filesUrl) => {
        set({
            filesInRequest: filesUrl,
        })
    },

    isCanFilesInRequest: false,
    setIsCanFilesInRequest: (isCan) => {
        set({
            isCanFilesInRequest: isCan,
        })
    },

    requestSettingAdditionalContexts: undefined,
    setRequestSettingAdditionalContexts: (requestSetting) => {
        set({
            requestSettingAdditionalContexts: requestSetting,
        })
    },
    requestMemoryMessageCount: 10,
    setRequestSettingMemoryMessageCount: (requestSetting) => {
        set({
            requestMemoryMessageCount: requestSetting,
        })
    },
    requestSettingRagEnabled: true,
    setRequestSettingRagEnabled: (requestSetting) => {
        set({
            requestSettingRagEnabled: requestSetting,
        })
    },
    requestSettingVoiceAnswer: false,
    setRequestSettingVoiceAnswer: (requestSetting) => {
        set({
            requestSettingVoiceAnswer: requestSetting,
        })
    },
    requestSettingContext: '',
    setRequestSettingContext: (requestSetting) => {
        set({
            requestSettingContext: requestSetting,
        })
    },

    requestSettingLlmTools: undefined,
    setRequestSettingLlmTools: (requestSetting) => {
        set({
            requestSettingLlmTools: requestSetting,
        })
    },

    setIsShowRegistrationDialog: (isShowRegistrationDialog) => {
        set({
            isShowRegistrationDialog,
        })
    },

    openRequestSetting: false,
    setOpenRequestSetting: (isOpen) => {
        set({
            openRequestSetting: isOpen,
        })
    },

    requestDetail: undefined,
    isLoadingRequestDetail: false,
    errorRequestDetail: undefined,
    fetchRequestDetail: async (params, shared) => {
        try {
            set({
                requestDetail: undefined,
                isLoadingRequestDetail: true,
                errorRequestDetail: undefined,
                requestMessages: undefined,
            })

            let data

            if (shared) {
                data = await getSharedThread(params)
            } else {
                data = await getRequestDetail(params)
            }

            set({
                requestDetail: data,
                isLoadingRequestDetail: false,
            })
        } catch (error) {
            set({
                isLoadingRequestDetail: false,
                errorRequestDetail: 'Ошибка получения деталей запроса',
            })
        }
    },

    requestMessages: undefined,
    isLoadingSharedThreadMessages: false,
    errorRequestMessages: undefined,
    fetchSharedThreadMessages: async (params) => {
        try {
            set({
                requestMessages: undefined,
                isLoadingSharedThreadMessages: true,
                errorRequestMessages: undefined,
                totalMessageCount: 0,
            })

            const data = await getSharedReplyList(params)

            set({
                requestMessages: data.objects.reverse(),
                isLoadingSharedThreadMessages: false,
                totalMessageCount: data.total_count,
            })
        } catch (error) {
            set({
                isLoadingSharedThreadMessages: false,
                errorRequestMessages: 'Ошибка получения сообщений запроса',
            })
        }
    },

    setRequestMessages: (data, total) => {
        set({
            requestMessages: data,
            isLoadingSharedThreadMessages: false,
            totalMessageCount: total,
        })
    },

    isLoadingCreateRequest: false,
    errorCreateRequest: undefined,
    createRequest: async (params) => {
        try {
            set({
                isLoadingCreateRequest: true,
                errorCreateRequest: undefined,
            })
            const thread = {
                workplace_id: params.workplace_id ?? null,
                assistant_id: params.assistant_id ?? null,
                name: params.name,
                agent: params.agent,
                memory_message_count: params.memory_message_count,
                rag_enabled: params.rag_enabled,
                voice_answer: params.voice_answer,
                context: params.context,
                params: params.params,
                additional_contexts: params.additional_contexts,
                deep_research_enabled: params.deep_research_enabled,
                speech_voice: params.speech_voice,
                voice_speed: params.voice_speed,
            }

            const data = await postThead(thread, { min_balance: params?.min_balance })

            set({
                isLoadingCreateRequest: false,
            })

            return data
        } catch (error) {
            if (error?.response?.data?.detail?.error_code === 'GuestModeLimit') {
                set({
                    isLoadingCreateRequestMessage: false,
                    isShowRegistrationDialog: true,
                })
            } else {
                set({
                    isLoadingCreateRequest: false,
                    errorCreateRequest:
                        error?.response?.data?.detail?.error_code === 'NoBalanceForOperation'
                            ? 'Недостаточно средств для проведения операции, требуется пополнить баланс'
                            : 'Ошибка создания нового запроса',
                })
            }
        }
    },

    isRemoveRequest: false,
    isLoadingRemoveRequest: false,
    errorRemoveRequest: undefined,
    removeRequest: async (params) => {
        try {
            set({
                isRemoveRequest: false,
                isLoadingRemoveRequest: true,
                errorRemoveRequest: undefined,
            })

            await deleteThread(params)
            set({
                requestMessages: undefined,
                requestDetail: undefined,
                isRemoveRequest: true,
                isLoadingRemoveRequest: false,
            })
        } catch (error) {
            set({
                isRemoveRequest: false,
                isLoadingCreateRequest: false,
                errorRemoveRequest: 'Ошибка удаления запроса',
            })
        }
    },

    setIsDoneUpdateRequest: (isDone) => {
        set({
            isDoneUpdateRequest: isDone,
        })
    },
    isDoneUpdateRequest: false,
    isLoadingUpdateRequest: false,
    errorUpdateRequest: undefined,
    updateRequest: async (params, payload) => {
        try {
            set({
                isDoneUpdateRequest: false,
                isLoadingUpdateRequest: true,
                errorUpdateRequest: undefined,
            })
            const data = await patchThead(params, payload)

            set({
                isDoneUpdateRequest: true,
                requestDetail: {
                    ...data,
                    assistant_id: has(payload, 'assistant_id')
                        ? payload.assistant_id
                        : data?.assistant_id,
                },
                isLoadingUpdateRequest: false,
            })
        } catch (error) {
            set({
                isLoadingUpdateRequest: false,
                errorUpdateRequest: 'Ошибка обновления деталей запроса',
            })
        }
    },

    isLoadingCreateRateReply: false,
    errorCreateRateReply: undefined,
    createRateReply: async (params, payload) => {
        try {
            set({
                isLoadingCreateRateReply: true,
                errorCreateRateReply: undefined,
            })

            const data = await postRateReply(params, payload)

            const requestMessagesBuffer = get().requestMessages ?? []
            if (data.parental_reply_id) {
                const requestIndexEdit = findIndex(requestMessagesBuffer, {
                    id: data.parental_reply_id,
                })
                const requestParentalIndexEdit = findIndex(
                    requestMessagesBuffer[requestIndexEdit].linked_replies,
                    { id: params.replyId },
                )
                requestMessagesBuffer[requestIndexEdit].linked_replies[
                    requestParentalIndexEdit
                ].score = data.score
                set({
                    requestMessages: requestMessagesBuffer,
                    isLoadingCreateRateReply: false,
                })
            } else {
                const requestIndexEdit = findIndex(requestMessagesBuffer, { id: params.replyId })
                requestMessagesBuffer[requestIndexEdit].score = data.score
                set({
                    requestMessages: requestMessagesBuffer,
                    isLoadingCreateRateReply: false,
                })
            }

            set({
                requestMessages: requestMessagesBuffer,
                isLoadingCreateRateReply: false,
            })
        } catch (error) {
            set({
                isLoadingCreateRateReply: false,
                errorCreateRateReply: 'Ошибка оценки',
            })
        }
    },

    isDoneRemoveReply: false,
    isLoadingRemoveReply: false,
    errorRemoveReply: undefined,
    removeReply: async (replyIds, softDelete) => {
        try {
            set({
                isDoneRemoveReply: false,
                isLoadingRemoveReply: true,
                errorRemoveReply: undefined,
            })

            if (softDelete) {
                await deleteReply(replyIds)
            } else {
                await deleteHardReply(replyIds)
            }
            const requestMessagesBuffer = get().requestMessages ?? []

            const data = softDelete
                ? requestMessagesBuffer.map((item) =>
                      item.id === replyIds[0] ? { ...item, is_deleted: true } : item,
                  )
                : requestMessagesBuffer.filter((item) => item.id !== replyIds[0])

            set({
                requestMessages: data,
                isDoneRemoveReply: true,
                isLoadingRemoveReply: false,
            })
        } catch (error) {
            set({
                isLoadingRemoveReply: false,
                errorRemoveReply: 'Ошибка',
            })
        }
    },
    removeReturnReply: async (replyId) => {
        try {
            set({
                isDoneRemoveReply: false,
                isLoadingRemoveReply: true,
                errorRemoveReply: undefined,
            })

            await removeReturnReply(replyId)
            const requestMessagesBuffer = get().requestMessages ?? []

            const data = requestMessagesBuffer.map((item) =>
                item.id === replyId ? { ...item, is_deleted: false } : item,
            )

            set({
                requestMessages: data,
                isDoneRemoveReply: true,
                isLoadingRemoveReply: false,
            })
        } catch (error) {
            set({
                isLoadingRemoveReply: false,
                errorRemoveReply: 'Ошибка',
            })
        }
    },

    isCreateRequestMessage: false,
    isLoadingCreateRequestMessage: false,
    errorCreateRequestMessage: undefined,
    createRequestMessage: async (params, min_balance) => {
        try {
            set({
                isCreateRequestMessage: false,
                isLoadingCreateRequestMessage: true,
                errorCreateRequestMessage: undefined,
            })

            const data = await postReply(params, min_balance)
            const requestMessageOld = get().requestMessages ?? []
            set({
                requestMessages: [data?.agent_reply, data?.user_reply, ...requestMessageOld],
                isCreateRequestMessage: true,
                isLoadingCreateRequestMessage: false,
            })
        } catch (error) {
            if (error?.response?.data?.detail?.error_code === 'GuestModeLimit') {
                set({
                    isLoadingCreateRequestMessage: false,
                    isShowRegistrationDialog: true,
                })
            } else {
                set({
                    isLoadingCreateRequestMessage: false,
                    errorCreateRequestMessage:
                        error?.response?.data?.detail?.error_message ??
                        'Ошибка добавления нового сообщения в запрос',
                })
            }
        }
    },
    clearRequest: () => {
        set({
            requestDetail: undefined,
        })
    },
    clearRequestSetting: () => {
        set({
            requestSettingAdditionalContexts: undefined,
            requestSettingParams: undefined,
            requestMemoryMessageCount: 10,
            requestSettingRagEnabled: true,
            requestSettingVoiceAnswer: false,
            requestSettingContext: '',
            requestSettingLlmTools: undefined,
        })
    },
    messageCitations: [],
    setMessageCitations: (messageCitations: ReplyContentTextDTO['citations']) =>
        set({ messageCitations }),

    updateRegularRequestsCountInRequestDetail: async (params) => {
        try {
            set({
                errorRequestDetail: undefined,
            })

            const data = await getRequestDetail(params)

            set({
                requestDetail: data,
            })
        } catch (error) {
            set({
                errorRequestDetail: 'Ошибка получения деталей запроса',
            })
        }
    },

    regenerateRequestMessage: async (params, minBalance, isEditText = false) => {
        try {
            const data = await postReply(params, minBalance)

            const newReply = data?.agent_reply
            const requestMessageOld = get().requestMessages ?? []

            const firstAssistantIndex = requestMessageOld.findIndex(
                (item) => item.actor === 'ASSISTANT',
            )

            set({
                requestMessages: requestMessageOld.map((reply, index) => {
                    if (reply.id === params.parental_reply_id) {
                        return {
                            ...reply,
                            content: reply?.content?.map((item) =>
                                item.content_type === 'TEXT'
                                    ? { ...item, text: params.text }
                                    : item,
                            ),
                            linked_replies: reply.linked_replies?.length
                                ? [...reply.linked_replies, newReply]
                                : [newReply],
                        }
                    } else if (index === firstAssistantIndex && firstAssistantIndex !== -1) {
                        return {
                            ...reply,
                            status: 'IN_PROCESS',
                        }
                    }
                    return reply
                }),
            })
        } catch (error) {
            set({
                errorRegenerateRequestMessage: 'Ошибка перегенерации запроса',
            })
        }
    },
}))
